import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import "./CustomStateSelect.css";

const CustomStateSelect = ({ value, onChange, options, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((o) => !o);
    if (!isOpen) setSearchTerm("");
  };

  const handleSelect = (opt) => {
    if (onChange) {
      onChange({
        target: {
          name: "state",
          value: opt,
        },
      });
    }
    setIsOpen(false);
  };

  const filteredOptions = (options || []).filter((opt) =>
    opt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="custom-state-select-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-state-select-trigger${isOpen ? " active" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-label="Select state"
      >
        <span className={value ? "css-val-selected" : "css-val-placeholder"}>
          {value || "Select state"}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`css-chevron${isOpen ? " css-chevron--open" : ""}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom-state-select-dropdown">
          <div className="custom-state-search-wrapper">
            <svg
              className="css-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search state…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="custom-state-search-input"
              autoFocus
            />
          </div>
          <ul className="custom-state-options-list" role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = value === opt;
                return (
                  <li
                    key={opt}
                    role="option"
                    aria-selected={isSelected}
                    className={`custom-state-option-item${
                      isSelected ? " state-selected" : ""
                    }`}
                    onClick={() => handleSelect(opt)}
                  >
                    <span className="css-opt-name">{opt}</span>
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#2a71ff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="css-check-icon"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="custom-state-no-options">No matches found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

CustomStateSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

export default CustomStateSelect;
